import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaBook, FaLock, FaBolt, FaMobileAlt, FaCode, FaRocket, FaChevronRight, FaGavel, FaBars, FaTimes, FaLayerGroup, FaArrowLeft } from 'react-icons/fa';
import AOS from 'aos';
import 'aos/dist/aos.css';
import IntegrationReport from './IntegrationReport';
import './Docs.css';

const Docs = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    window.scrollTo(0, 0);
  }, []);

  const toggleSidebar = () => setSidebarOpen(!isSidebarOpen);


  const sections = [
    { id: 'overview', title: 'What is Bharat Connect?', icon: <FaBook /> },
    { id: 'sampark', title: 'Using Sampark AI', icon: <FaBolt /> },
    { id: 'security', title: 'Privacy & Security', icon: <FaLock /> },
    { id: 'mobile', title: 'Using on your Phone', icon: <FaMobileAlt /> },
    { id: 'legal', title: 'Legal & Terms', icon: <FaGavel /> },
    { id: 'roadmap', title: 'What is coming next?', icon: <FaRocket /> },
  ];

  return (
    <div className={`docs-page ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      <button className="sidebar-toggle" onClick={toggleSidebar}>
        {isSidebarOpen ? <FaTimes /> : <FaBars />}
      </button>

      <div className={`sidebar-overlay ${isSidebarOpen ? 'active' : ''}`} onClick={toggleSidebar}></div>

      <div className={`docs-nav-sticky ${isSidebarOpen ? 'open' : ''}`}>
        <div className="docs-logo">
          <Link to="/" className="back-link-docs">
            <FaArrowLeft /> Home
          </Link>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            <span className="logo-icon">🇮🇳</span>
            <span className="logo-text">Bharat Docs</span>
          </div>
        </div>
        <nav className="docs-sidebar">
          {sections.map((section) => (
            <a 
              key={section.id} 
              href={`#${section.id}`} 
              className="nav-link"
              onClick={() => setSidebarOpen(false)}
            >
              {section.icon}
              <span>{section.title}</span>
              <FaChevronRight className="chevron" />
            </a>
          ))}

          {/* Upgrade Card */}
          <div className="sidebar-upgrade-card">
            <div className="upgrade-card-header">
              <div className="upgrade-logo">
                <FaRocket style={{fontSize: '16px', color: 'white'}} />
              </div>
              <button className="upgrade-close-btn"><FaTimes /></button>
            </div>
            <h4>Discover v2.1.0</h4>
            <p>Explore the new settings hub, UI refinements, and faster AI.</p>
            <Link to="/version" className="upgrade-action-btn" style={{display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box'}}>View Changelog</Link>
          </div>
        </nav>
      </div>

      <main className="docs-content">
        <header className="docs-header" data-aos="fade-down">
          <h1>Help & Features Guide</h1>
          <p>Everything you need to know about using Bharat Connect.</p>
          <div className="version-badge">v2.1.0 Stable</div>
        </header>

        <section id="overview" className="docs-section" data-aos="fade-up">
          <h2><FaBook /> What is Bharat Connect?</h2>
          <div className="glass-card docs-card">
            <p>Bharat Connect is a fast, secure, and easy-to-use messaging app designed for everyone. It allows you to chat with friends, family, and colleagues instantly, no matter where you are.</p>
            <div className="feature-highlight-grid">
              <div className="highlight-item">
                <strong>Fast</strong>
                <span>Messages arrive instantly</span>
              </div>
              <div className="highlight-item">
                <strong>Reliable</strong>
                <span>Works on any network</span>
              </div>
              <div className="highlight-item">
                <strong>Private</strong>
                <span>Your chats are totally secure</span>
              </div>
            </div>
          </div>
        </section>
 
        <section id="sampark" className="docs-section" data-aos="fade-up">
          <h2><FaBolt /> Using Sampark AI</h2>
          <div className="glass-card docs-card">
            <h3>Your Personal Smart Assistant</h3>
            <p>Sampark AI is a smart helper built right into Bharat Connect. You can ask it questions, have it summarize long conversations, or even write messages for you.</p>
            
            <div className="doc-feature-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
              <div className="doc-feature">
                <h4 style={{ color: '#00a884' }}>The AI Dashboard</h4>
                <p>Click on <strong>Sampark AI Hub</strong> in the menu to talk to the AI privately on its own special screen.</p>
              </div>
              <div className="doc-feature">
                <h4 style={{ color: '#00ccff' }}>Chat with AI</h4>
                <p>Type <code>@Sampark AI</code> in any chat to bring the smart assistant directly into your conversation.</p>
              </div>
            </div>

            <div className="pro-tip" style={{ marginTop: '20px', padding: '15px', background: 'rgba(0, 168, 132, 0.1)', borderLeft: '4px solid #00a884', borderRadius: '4px' }}>
              <strong>Pro Tip:</strong> Use the "@" symbol on your keyboard to quickly tag the AI and ask it a question!
            </div>
          </div>
        </section>

        <section id="security" className="docs-section" data-aos="fade-up">
          <h2><FaLock /> Privacy & Security</h2>
          <div className="glass-card docs-card">
            <h3>Totally Private Chats</h3>
            <p>We believe your conversations are your business. That is why every message you send is scrambled with a digital lock before it leaves your phone. Only the person you are sending it to has the key to unlock and read it.</p>
            <ul>
              <li>No one else can read your messages. Not even us.</li>
              <li>You can easily hide your "Last Seen" and profile photo in the Settings menu.</li>
              <li>Your data is safe and secure at all times.</li>
            </ul>
          </div>
        </section>

        <section id="mobile" className="docs-section" data-aos="fade-up">
          <h2><FaMobileAlt /> Using on your Phone</h2>
          <div className="glass-card docs-card">
            <p>Bharat Connect works beautifully on both your computer and your mobile phone without needing to download a massive app from the App Store.</p>
            <p>Simply open the website on your phone, log in, and you can chat from anywhere. Your messages will save automatically and sync to your computer when you get home.</p>
          </div>
        </section>

        <section id="legal" className="docs-section" data-aos="fade-up">
          <h2><FaGavel /> Legal & Terms</h2>
          <div className="glass-card docs-card">
            <p>We are completely transparent about how we operate. Click below to read our simple, plain-English policies.</p>
            <div className="legal-links-grid">
              <div className="legal-card">
                <h4>Privacy Policy</h4>
                <p>Learn how we protect your data.</p>
                <Link to="/privacy" className="v-link">Read Privacy Policy <FaChevronRight /></Link>
              </div>
              <div className="legal-card">
                <h4>Terms of Service</h4>
                <p>Review the rules for using Bharat Connect.</p>
                <Link to="/terms" className="v-link">Read Terms of Service <FaChevronRight /></Link>
              </div>
            </div>
          </div>
        </section>

        <section id="roadmap" className="docs-section" data-aos="fade-up">
          <h2><FaRocket /> What is coming next?</h2>
          <div className="roadmap-timeline">
            <div className="roadmap-item active">
              <div className="roadmap-marker"></div>
              <div className="roadmap-content">
                <h4>Coming Soon</h4>
                <p>Voice and Video Calling right from your browser.</p>
              </div>
            </div>
            <div className="roadmap-item">
              <div className="roadmap-marker"></div>
              <div className="roadmap-content">
                <h4>Later This Year</h4>
                <p>Group chats with up to 500 people.</p>
              </div>
            </div>
          </div>
        </section>
        
        <footer className="docs-footer">
          <p>© 2026 Bharat Connect. Need help? <a href="mailto:support@bharatconnect.in">Contact support</a></p>
        </footer>
      </main>
    </div>
  );
};

export default Docs;
